import Typography from '@mui/material/Typography'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import FlashCard from 'pagesNew/FlashCards'
import TeamMembers from 'pagesNew/TeamMembers'
import UpgradePage from 'pagesNew/UpgradePage'
import {
  OrganizeCreateDentalSchoolStatistic,
  OrganizeCreateStudyGuide,
  OrganizeCreateSuccessStory,
  OrganizeEditDentalSchoolStatistic,
  OrganizeEditStudyGuide,
  OrganizeEditSuccessStory,
} from 'pagesNew/OrginizeBlogPages'
import { SuccessStoryCreate, SuccessStoryEdit } from 'pagesNew/SuccessStory'
import { StudyGuideCreate, StudyGuideEdit } from 'pagesNew/StudyGuide'
import SignIn from 'pagesNew/SignIn/SignIn'
import { NotFound } from 'components/NotFound'
import { AuthGuard } from 'components/AuthGuard'
import { PracticeTestDetails, UserEdit, UserList } from 'pagesNew/Users'
import { CreatePATStrategy, EditPATStrategy, StrategiesList } from 'pagesNew/Strategies'
import PATGenerators from 'pagesNew/PATGenerators'
import FullLengthTests from 'pagesNew/FullLengthTests'
import { VideoContent } from 'pagesNew/VideoContent'
import { MarkedQuestions } from 'pagesNew/MarkedQuestions'
import { PastTestResults } from 'pagesNew/PastTestResults'
import { Performance } from 'pagesNew/Performance'
import { CreateExtension, EditAdditionalTest, EditMembership, EditPauseToken, Products } from 'pagesNew/Products'
import RecommendedArticles from 'pagesNew/RecommendedArticles'
import { Emails } from 'pagesNew/Emails'
import { InactivityTracker } from 'utils/InactivityTracker'
import { AchievementCreate, AchievementEdit, Achievements, RewardCreate, RewardEdit } from 'pagesNew/Achievements'
import { DailyWarmup } from 'pagesNew/DailyWarmup'
import { AccountSharing } from 'pagesNew/AccountSharing'
import { Create as CommonPTCreate, Edit as CommonPTEdit, List as CommonPTList } from 'pagesNew/CommonPracticeTests'
import { GameChallenges } from 'pagesNew/GameChallenges'
import { CreateTest } from 'pagesNew/CreateTest'
import { ResponseTests } from 'pagesNew/ResponseTests'
import { CreateResponseTest } from 'pagesNew/ResponseTests/Create'
import { EditResponseTest } from 'pagesNew/ResponseTests/Edit'
import { Dashboard as ResponseFullLengthTestDashboard } from 'pagesNew/ResponseFullLengthTest/Dashboard'
import { TrainingData } from 'pagesNew/TrainingData'
import { TrainingGradeEdit } from 'pagesNew/TrainingData/TrainingGradeEdit'
import { GPTGradeEdit } from 'pagesNew/TrainingData/GPTGradeEdit'
import { PracticeResults } from 'pagesNew/PracticeResults'
import { ContentCompletion } from 'pagesNew/ContentCompletion'
import { ProtectedRoute } from 'components/ProtectedRoute/ProtectedRoute'
import { ADMIN_GROUP, AI_TRAINER, MARKETING_GROUP, SUPER_ADMIN_GROUP } from 'utils/services/auth'
import { DailyGoal } from 'pagesNew/DailyGoal'
import { InfluencerHub } from 'pagesNew/InfluencerHub'
import { StudyScheduleTabs } from 'pagesNew/StudySchedule'
import { Dashboard as CARSPluginDashboard } from 'pagesNew/CARSPlugin/Dashboard'
import { ExamSection as CARSPluginExamSection } from 'pagesNew/CARSPlugin/ExamSection'
import { Statistics } from './pagesNew/Statistics'
import { Layout } from './components/Layout/Layout'
import CheatSheets from './pagesNew/CheatSheets'
import CommonElements from './pagesNew/CommonElements'
import { CrashCourseCreate, CrashCourseEdit, CrashCourseList } from './pagesNew/CrashCourses'
import {
  OSCEMultiselectQBankList,
  OSCERxWritingQBankList,
  OSCEStandaloneQBankList,
  CommunityTestletsList,
  QuestionBankCreate,
  QuestionBankEdit,
  QuestionBankList,
} from './pagesNew/QuestionBanks'
import { CASE_BASED_SCENARIOS_TAB_INDEX, COMMON_QB_TAB_INDEX } from './pagesNew/QuestionBanks/utils'
import { PracticeTestCreate, PracticeTestEdit, PracticeTestList } from './pagesNew/PracticeTests'
import QuestionCategories from './pagesNew/QuestionCategories'
import { QuestionCreate, QuestionEdit, QuestionTabs, QuestionTemplateEdit } from './pagesNew/Questions'
import { PromoCodeAnalytics, PromoCodeCreate, PromoCodeEdit, PromoCodeList } from './pagesNew/PromoCodes'
import StudyNotes from './pagesNew/StudyNotes'
import { BlogPages } from './pagesNew/BlogPages'
import VideoContentCreate from './pagesNew/Video/VideoContent/VideoContentCreate'
import VideoContentEdit from './pagesNew/Video/VideoContent/VideoContentEdit'
import VideoDashboard from './pagesNew/Video/VideoDashboard'
import VideoQuizCreate from './pagesNew/Video/VideoQuiz/VideoQuizCreate'
import VideoQuizEdit from './pagesNew/Video/VideoQuiz/VideoQuizEdit'
import VideoResourceCreate from './pagesNew/Video/VideoResource/VideoResourceCreate'
import VideoResourceEdit from './pagesNew/Video/VideoResource/VideoResourceEdit'
import ChapterCompletionEdit from './pagesNew/Video/ChapterCompletion/ChapterCompletionEdit'
import { CanadianDatTips } from './pagesNew/CanadianDatTips'
import { Maintenance } from './pagesNew/Maintenance'
import { cmsRoutes } from './routes/cmsRoutes'
import { ADMISSION_DENTISTRY, ADMISSION_OPTOMETRY, AdmissionManagement } from './pagesNew/AdmissionManagement'
import { CaseBasedScenario } from './pagesNew/CaseBasedScenario'
import { DentalSchoolCreate, DentalSchoolEdit } from './pagesNew/DentalSchools'
import { ChapterNotes } from './pagesNew/ChapterNotes'
import { TrainAIModel } from './pagesNew/TrainAIModel'
import { FAQPage } from './pagesNew/FAQ/FAQPage'
import { EditInstitutionalCode, InstitutionalCode } from './pagesNew/InstitutionalCodes'
import {
  CARSQuestionCreate,
  CARSQuestionEdit,
  CARSQuestionTemplateEdit,
  QuestionPassageList,
} from './pagesNew/QuestionPassages'
import {
  QuestionPassageBankCreate,
  QuestionPassageBankEdit,
  QuestionPassageBankList,
} from './pagesNew/QuestionPassageBanks'
import { PracticeTestTypeEnum } from './__generatedTypes__/globalTypes'

const BASE_ADMIN_GROUPS = [ADMIN_GROUP, SUPER_ADMIN_GROUP]
const BASE_ADMIN_OR_MARKETING_GROUPS = [...BASE_ADMIN_GROUPS, MARKETING_GROUP]
const SUPER_ADMIN_GROUPS = [SUPER_ADMIN_GROUP]
const AI_TRAINER_GROUPS = [AI_TRAINER]

const ALL_GROUPS = [...BASE_ADMIN_GROUPS, ...AI_TRAINER_GROUPS]

const GuardedRoutes = () => (
  <AuthGuard>
    <InactivityTracker />
    <Routes>
      <Route path="/" element={<Layout />} />
      <Route path={cmsRoutes.MAINTENANCE} element={<Maintenance />} />
      <Route path={`${cmsRoutes.PLATFORM}`} element={<Layout />}>
        {/* TODO: update platform index screen */}
        <Route path="" element={<Typography>platform index</Typography>} />
        <Route
          path={cmsRoutes.BLOG_PAGES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <BlogPages />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_CREATE_SUCCESS_STORIES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <OrganizeCreateSuccessStory />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_EDIT_SUCCESS_STORIES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <OrganizeEditSuccessStory />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_CREATE_STUDY_GUIDES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <OrganizeCreateStudyGuide />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_EDIT_STUDY_GUIDES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <OrganizeEditStudyGuide />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_CREATE_DENTAL_SCHOOL_STATISTICS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <OrganizeCreateDentalSchoolStatistic />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_EDIT_DENTAL_SCHOOL_STATISTICS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <OrganizeEditDentalSchoolStatistic />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_CREATE_OPTOMETRY_SCHOOL_STATISTICS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <OrganizeCreateDentalSchoolStatistic isOptometry />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ORGANIZE_EDIT_OPTOMETRY_SCHOOL_STATISTICS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <OrganizeEditDentalSchoolStatistic isOptometry />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CREATE_SUCCESS_STORY}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <SuccessStoryCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.EDIT_SUCCESS_STORY}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <SuccessStoryEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CREATE_STUDY_GUIDE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <StudyGuideCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.EDIT_STUDY_GUIDE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <StudyGuideEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CREATE_PAT_STRATEGY}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CreatePATStrategy />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.EDIT_PAT_STRATEGY}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <EditPATStrategy />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CREATE_DENTAL_SCHOOL}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <DentalSchoolCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.EDIT_DENTAL_SCHOOL}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <DentalSchoolEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CREATE_OPTOMETRY_SCHOOL}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <DentalSchoolCreate isOptometry />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.EDIT_OPTOMETRY_SCHOOL}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <DentalSchoolEdit isOptometry />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.STUDY_NOTE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <StudyNotes />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.FLASH_CARD}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <FlashCard />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CHEAT_SHEET}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CheatSheets />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CHAPTER_NOTES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <ChapterNotes />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_CATEGORY}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionCategories />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionTabs />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_TEMPLATE_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionTemplateEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_QUESTION_TEMPLATE_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CARSQuestionTemplateEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_QUESTION_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionPassageList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_QUESTION_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CARSQuestionCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_QUESTION_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CARSQuestionEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_QUESTION_BANK_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionPassageBankList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_QUESTION_BANK_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionPassageBankCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_QUESTION_BANK_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionPassageBankEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_GAMES_STATS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <Statistics />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.STATISTICS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <Statistics />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_BANK_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionBankList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_BANK_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionBankCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.QUESTION_BANK_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <QuestionBankEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CASE_BASED_SCENARIO_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CaseBasedScenario tabIndex={CASE_BASED_SCENARIOS_TAB_INDEX} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.SCENARIOS_QB_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CaseBasedScenario tabIndex={COMMON_QB_TAB_INDEX} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.COMMUNITY_TESTLETS_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommunityTestletsList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.OSCE_STANDALON_QB_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <OSCEStandaloneQBankList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.OSCE_MULTISELECT_QB_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <OSCEMultiselectQBankList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.OSCE_RX_WRITING_QB_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <OSCERxWritingQBankList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.STRATEGIES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <StrategiesList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.RECOMMENDED_ARTICLES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <RecommendedArticles />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRACTICE_TEST_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PracticeTestList type={PracticeTestTypeEnum.PracticeTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRACTICE_TEST_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PracticeTestCreate type={PracticeTestTypeEnum.PracticeTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRACTICE_TEST_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PracticeTestEdit type={PracticeTestTypeEnum.PracticeTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.MINI_TEST_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PracticeTestCreate type={PracticeTestTypeEnum.MiniTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.MINI_TEST_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PracticeTestEdit type={PracticeTestTypeEnum.MiniTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.COMMON_ELEMENTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <CommonElements />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CRASH_COURSE_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CrashCourseList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CRASH_COURSE_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CrashCourseCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CRASH_COURSE_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CrashCourseEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.USER_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <UserList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.USER_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <UserEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.USER_EDIT_PT_DETAILS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <PracticeTestDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ACCOUNT_SHARING}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <AccountSharing />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRODUCTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <Products />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRODUCTS_MEMBERSHIP_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <EditMembership />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRODUCTS_EXTENSION_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CreateExtension />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRODUCTS_EXTENSION_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <EditMembership isExtension />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRODUCTS_ADDITIONAL_TESTS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <EditAdditionalTest />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRODUCTS_PAUSE_TOKEN_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <EditPauseToken />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PROMO_CODES_LIST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <PromoCodeList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PROMO_CODES_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <PromoCodeCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PROMO_CODES_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <PromoCodeEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PROMO_CODES_ANALYTICS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_OR_MARKETING_GROUPS}>
              <PromoCodeAnalytics />
            </ProtectedRoute>
          }
        />
        <Route path={cmsRoutes.VIDEO_DASHBOARD}>
          <Route
            path={cmsRoutes.VIDEO_DASHBOARD}
            element={
              <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                <VideoDashboard />
              </ProtectedRoute>
            }
          />
          <Route path={cmsRoutes.VIDEOS_VIDEO_CATEGORY}>
            <Route
              path={cmsRoutes.VIDEOS_RESOURCE}
              element={
                <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                  <VideoResourceCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={cmsRoutes.VIDEOS_RESOURCE_EDIT}
              element={
                <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                  <VideoResourceEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={cmsRoutes.VIDEOS_VIDEO}
              element={
                <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                  <VideoContentCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={cmsRoutes.VIDEOS_VIDEO_EDIT}
              element={
                <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                  <VideoContentEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={cmsRoutes.VIDEOS_QUIZ}
              element={
                <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                  <VideoQuizCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={cmsRoutes.VIDEOS_QUIZ_EDIT}
              element={
                <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                  <VideoQuizEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={cmsRoutes.VIDEOS_CHAPTER_COMPLETION_EDIT}
              element={
                <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
                  <ChapterCompletionEdit />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        <Route
          path={cmsRoutes.CANADIAN_DAT_TIPS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CanadianDatTips />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.MARKED_QUESTIONS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <MarkedQuestions />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.VIDEO_CONTENT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <VideoContent />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.COMMON_PTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTList />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.COMMON_PTS_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.COMMON_PTS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PLATFORM_MOCK}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTList type={PracticeTestTypeEnum.MockTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PLATFORM_MOCK_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTCreate type={PracticeTestTypeEnum.MockTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PLATFORM_MOCK_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTEdit type={PracticeTestTypeEnum.MockTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.IN_PERSON_MOCK}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTList type={PracticeTestTypeEnum.InPersonMockTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.IN_PERSON_MOCK_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTCreate type={PracticeTestTypeEnum.InPersonMockTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.IN_PERSON_MOCK_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTEdit type={PracticeTestTypeEnum.InPersonMockTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTList type={PracticeTestTypeEnum.CarsPracticeTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PTS_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTCreate type={PracticeTestTypeEnum.CarsPracticeTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PTS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTEdit type={PracticeTestTypeEnum.CarsPracticeTest} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PPS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTList type={PracticeTestTypeEnum.CarsPracticePassage} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PPS_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTCreate type={PracticeTestTypeEnum.CarsPracticePassage} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PPS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTEdit type={PracticeTestTypeEnum.CarsPracticePassage} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.DAILY_CARS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTList type={PracticeTestTypeEnum.DailyCarsPractice} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.DAILY_CARS_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTCreate type={PracticeTestTypeEnum.DailyCarsPractice} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.DAILY_CARS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CommonPTEdit type={PracticeTestTypeEnum.DailyCarsPractice} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CREATE_TEST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CreateTest />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PAT_GENERATORS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PATGenerators />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.FULL_LENGTH_TESTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <FullLengthTests />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PAST_TEST_RESULTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PastTestResults />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PRACTICE_RESULTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <PracticeResults />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.PERFORMANCE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <Performance />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ACHIEVEMENTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <Achievements />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ACHIEVEMENTS_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <AchievementCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.ACHIEVEMENTS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <AchievementEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.REWARDS_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <RewardCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.REWARDS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <RewardEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.DAILY_WARMUP}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <DailyWarmup />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.DAILY_GOAL}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <DailyGoal />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.GAME_CHALLENGES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <GameChallenges />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CONTENT_COMPLETION}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <ContentCompletion />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.TRAINING_DATA}
          element={
            <ProtectedRoute allowedForGroups={ALL_GROUPS}>
              <TrainingData />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.TRAINING_GRADE_EDITOR}
          element={
            <ProtectedRoute allowedForGroups={ALL_GROUPS}>
              <TrainingGradeEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.GPT_GRADE_EDITOR}
          element={
            <ProtectedRoute allowedForGroups={ALL_GROUPS}>
              <GPTGradeEdit />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.RESPONSE_TESTS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <ResponseTests />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.RESPONSE_TESTS_CREATE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CreateResponseTest />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.RESPONSE_TESTS_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <EditResponseTest />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.RESPONSE_FULL_LENGTH_TEST}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <ResponseFullLengthTestDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.INFLUENCER_HUB}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <InfluencerHub />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.STUDY_SCHEDULE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <StudyScheduleTabs />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PLUGIN}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CARSPluginDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.CARS_PLUGIN_EXAM_SECTION}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <CARSPluginExamSection />
            </ProtectedRoute>
          }
        />

        <Route
          path={cmsRoutes.EMAILS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <Emails />
            </ProtectedRoute>
          }
        />

        <Route
          path={cmsRoutes.DENTISTRY}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <AdmissionManagement type={ADMISSION_DENTISTRY} />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.OPTOMETRY}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <AdmissionManagement type={ADMISSION_OPTOMETRY} />
            </ProtectedRoute>
          }
        />

        <Route
          path={cmsRoutes.TEAM_MEMBERS}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <TeamMembers />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.UPGRADE_PAGE}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <UpgradePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.TRAIN_AI_MODEL}
          element={
            <ProtectedRoute allowedForGroups={SUPER_ADMIN_GROUPS}>
              <TrainAIModel />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.FAQ}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <FAQPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={cmsRoutes.INSTITUTIONAL_CODES}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <InstitutionalCode />
            </ProtectedRoute>
          }
        />
        <Route
          path={cmsRoutes.INSTITUTIONAL_CODES_EDIT}
          element={
            <ProtectedRoute allowedForGroups={BASE_ADMIN_GROUPS}>
              <EditInstitutionalCode />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  </AuthGuard>
)

export const AppRouter = () => (
  <BrowserRouter>
    <Routes>
      <Route path={cmsRoutes.SIGN_IN} element={<SignIn />} />
      <Route path="*" element={<GuardedRoutes />} />
      <Route path={cmsRoutes.NOT_FOUND} element={<NotFound />} />
    </Routes>
  </BrowserRouter>
)
